// Utility functions for SaveRequest
import { fToTitleMultipleWords } from '../../../../utils/formatText';
import { fMonthDayYear, formatDateTimeWithoutTimezone, parseDateOnlyToLocalDate } from '../../../../utils/formatTime';

// Parses a date (optionally with time) as a plain EST wall-clock value; no timezone conversion is applied.
export const parseWallClockDateTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const [h, m, s] = timeStr ? timeStr.split(':').map(Number) : [0, 0, 0];
  return new Date(year, month - 1, day, h, m, s || 0, 0);
};

export const buildTransportRequestPayload = (formState, FIELDS) => {
  const payload = { ...formState };
  [
    FIELDS.PICKUP_TIME,
    FIELDS.RETURN_TRIP_PICKUP_TIME,
    FIELDS.APPOINTMENT_DATE_TIME,
    FIELDS.PICKUP_TIME_ORIGINAL,
    FIELDS.APPOINTMENT_DATE_TIME_ORIGINAL,
    FIELDS.SEEN_BY_DATE,
    FIELDS.RECURRENCE_END_DATE,
    FIELDS.OCCURRENCE_DATE,
    FIELDS.PARENT_APPOINTMENT_DATE,
  ].forEach((field) => {
    payload[field] = formatDateTimeWithoutTimezone(payload[field]);
  });

  return payload;
};

export const buildTransportStatusPayload = (statusData) => ({
  ...statusData,
  occurrenceDate: formatDateTimeWithoutTimezone(statusData?.occurrenceDate),
});

// Formats a Date object as YYYY-MM-DD in local time (for date pickers)
export const formatLocalDate = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// Parses YYYY-MM-DD as local date to avoid UTC day shifts in negative offsets.
export const parseDateOnlyAsLocal = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!DATE_ONLY_REGEX.test(trimmed)) return null;
  return new Date(`${trimmed}T00:00:00`);
};

// Sorts by label property
export const labelSort = (a, b) => a.label.localeCompare(b.label);

// Sanitizes date/time values
export const sanitizeDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val !== 'string' || val.trim() === '') return null;
  const parsed = Date.parse(val);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed);
};

// Maps incoming request to backend field names for formState
export const mapRequestToFormState = (req, initialFormState, FIELDS, PICKUP_TYPE_HOME, PICKUP_TYPE_DOCTOR) => {
  if (!req) return initialFormState;
  return {
    Id: req.id ?? undefined,
    [FIELDS.FACILITY_ID]: req.facilityId ?? null,
    [FIELDS.RESIDENT_IDS]: Array.isArray(req.residents) ? req.residents.map((r) => r.residentId) : [],
    [FIELDS.HAS_EXISTING_APPOINTMENT]: req.hasExistingAppointment ?? true,
    [FIELDS.PICKUP_TIME]: sanitizeDate(req.pickupTime),
    [FIELDS.APPOINTMENT_DATE_TIME]: sanitizeDate(req.appointmentDateTime),
    [FIELDS.PICKUP_TIME_ORIGINAL]: sanitizeDate(req.pickupTime),
    [FIELDS.APPOINTMENT_DATE_TIME_ORIGINAL]: sanitizeDate(req.appointmentDateTime),
    [FIELDS.SEEN_BY_DATE]: parseDateOnlyToLocalDate(req.seenByDate),
    [FIELDS.CREATE_RETURN_TRIP]: req.createReturnTrip ?? null,
    [FIELDS.AIDE_REQUIRED]: req.aideRequired ?? null,
    [FIELDS.UNIT_AIDE_REQUIRED]: req.unitAideRequired ?? null,
    [FIELDS.CNA_REQUIRED]: req.cnaRequired ?? null,
    [FIELDS.PCS_FORM]: req.pcsForm ?? false,
    [FIELDS.DETAIL_O2]: req.detailO2 ?? false,
    [FIELDS.DETAIL_SNACK]: req.detailSnack ?? false,
    [FIELDS.DETAIL_LUNCH]: req.detailLunch ?? false,
    [FIELDS.DETAIL_XRAYS]: req.detailXrays ?? false,
    [FIELDS.DETAIL_RESIDENT_DOCUMENTS]: req.detailResidentDocuments ?? false,
    [FIELDS.DETAIL_CUSHION]: req.detailCushion ?? false,
    [FIELDS.DETAIL_HOYER_LIFT_PAD]: req.detailHoyerLiftPad ?? false,
    [FIELDS.DETAIL_WHEELCHAIR]: req.detailWheelchair ?? false,
    [FIELDS.TRANSPORT_TYPE_ID]: req.transportTypeId ?? null,
    [FIELDS.TRANSPORT_TYPE_NAME]: req.transportTypeName ?? null,
    [FIELDS.PICKUP_LOCATION_TYPE]: req.pickupLocationType ?? PICKUP_TYPE_HOME,
    [FIELDS.PICKUP_DOCTOR_ID]: req.pickupDoctorId ?? null,
    [FIELDS.DROPOFF_LOCATION_TYPE]: req.dropoffLocationType ?? PICKUP_TYPE_DOCTOR,
    [FIELDS.DROPOFF_DOCTOR_ID]: req.dropoffDoctorId ?? null,
    [FIELDS.IS_OUTSOURCED]: req.isOutsourced ?? null,
    [FIELDS.OUTSOURCE_COMPANY_ID]: req.outsourceCompanyId ?? null,
    [FIELDS.VEHICLE_ID]: req.vehicleId ?? null,
    [FIELDS.ASSIGNED_DRIVER_ID]: req.assignedDriverId ?? null,
    [FIELDS.APPOINTMENT_DESC]: req.appointmentDesc ?? '',
    [FIELDS.IS_RECURRING]: req.isRecurring ?? false,
    [FIELDS.RECURRENCE_TYPE]: req.recurrenceType ?? null,
    [FIELDS.RECURRENCE_FREQUENCY]: req.recurrenceFrequency ?? null,
    [FIELDS.RECURRENCE_END_DATE]: sanitizeDate(req.recurrenceEndDate),
    [FIELDS.RECURRENCE_DAYS_OF_WEEK]: req.recurrenceDaysOfWeek ?? null,
    [FIELDS.RECURRENCE_DAY_OF_MONTH]: req.recurrenceDayOfMonth ?? null,
    [FIELDS.OCCURRENCE_DATE]: sanitizeDate(req.occurrenceDate),
    [FIELDS.OCCURRENCE_ID]: req.occurrenceId ?? null,
    [FIELDS.PARENT_APPOINTMENT_DATE]: sanitizeDate(req.parentAppointmentDate),
    [FIELDS.DATE_ADDED]: sanitizeDate(req.dateAdded),
    [FIELDS.ADDED_BY]: req.addedBy ?? null,
    [FIELDS.REQUEST_STATUS_ID]: req.requestStatusId ?? null,
  };
};

// Filters options by input value
export const filterOptions = (options, params) =>
  options.filter((opt) => opt.label.toLowerCase().includes(params.inputValue.toLowerCase()));

// Helper to get plural form of recurrence labels
export const getPluralLabel = (label, count) => {
  if (count === 1) return label;
  if (label === 'day') return 'days';
  if (label === 'week') return 'weeks';
  if (label === 'month') return 'months';
  return label;
};

/**
 * Helper to summarize recurrence for display
 * @param {boolean} isRecurring - Whether recurrence is enabled
 * @param {string} recurrenceType - The type of recurrence (Daily, Weekly, MonthlyByDate, MonthlyByWeekday)
 * @param {number} recurrenceFrequency - How often to repeat (e.g., 1, 2, 3)
 * @param {number|null} recurrenceDayOfMonth - Day of month for monthly recurrence
 * @param {string|null} recurrenceEndDate - End date for recurrence
 * @param {object} RECURRENCE_TYPE_VALUES - Constants for recurrence types
 * @returns {string} Readable recurrence summary
 */
export const getRecurrenceSummary = (
  isRecurring,
  recurrenceType,
  recurrenceFrequency,
  recurrenceDayOfMonth,
  recurrenceEndDate,
  RECURRENCE_TYPE_VALUES,
  recurrenceDaysOfWeek,
  recurrenceWeekOfMonth
) => {
  if (!isRecurring) return 'Does not repeat';

  const frequency = recurrenceFrequency || 1;
  let typeLabel = '';

  // Determine the type label based on RecurrenceType
  switch (recurrenceType) {
    case RECURRENCE_TYPE_VALUES.DAILY:
      typeLabel = getPluralLabel('day', frequency);
      break;
    case RECURRENCE_TYPE_VALUES.WEEKLY:
      typeLabel = getPluralLabel('week', frequency);
      break;
    case RECURRENCE_TYPE_VALUES.MONTHLY_BY_DATE:
      typeLabel = getPluralLabel('month', frequency);
      break;
    case RECURRENCE_TYPE_VALUES.MONTHLY_BY_WEEKDAY:
      typeLabel = getPluralLabel('month', frequency);
      break;
    default:
      typeLabel = 'interval';
  }

  let recurrenceDay = '';
  // Helper for weekday names
  const getWeekdayStr = (days) => {
    const weekdayNames = days.map((d) => {
      const dayNum = parseInt(d, 10);
      return Number.isNaN(dayNum)
        ? d
        : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayNum] || d;
    });
    if (weekdayNames.length === 1) return weekdayNames[0];
    if (weekdayNames.length === 2) return `${weekdayNames[0]} and ${weekdayNames[1]}`;
    if (weekdayNames.length > 2)
      return `${weekdayNames.slice(0, -1).join(', ')}, and ${weekdayNames[weekdayNames.length - 1]}`;
    return '';
  };

  if (recurrenceType === RECURRENCE_TYPE_VALUES.MONTHLY_BY_DATE && recurrenceDayOfMonth) {
    recurrenceDay = ` on day ${recurrenceDayOfMonth}`;
  } else if (
    recurrenceType === RECURRENCE_TYPE_VALUES.MONTHLY_BY_WEEKDAY &&
    Array.isArray(recurrenceDaysOfWeek) &&
    recurrenceDaysOfWeek.length > 0
  ) {
    const weekOrdinals = {
      1: 'first',
      2: 'second',
      3: 'third',
      4: 'fourth',
      5: 'last',
    };
    const ordinal = weekOrdinals[recurrenceWeekOfMonth] || '';
    recurrenceDay = ` on the ${ordinal} ${getWeekdayStr(recurrenceDaysOfWeek)}`;
  } else if (
    recurrenceType === RECURRENCE_TYPE_VALUES.WEEKLY &&
    Array.isArray(recurrenceDaysOfWeek) &&
    recurrenceDaysOfWeek.length > 0
  ) {
    recurrenceDay = ` on ${getWeekdayStr(recurrenceDaysOfWeek)}`;
  }
  const recurrenceEndDisplayDate = parseDateOnlyAsLocal(recurrenceEndDate) || sanitizeDate(recurrenceEndDate);
  const reccurenceEndDesc = recurrenceEndDisplayDate ? `. Ends on ${fMonthDayYear(recurrenceEndDisplayDate)}` : '';
  const summary = `Repeats every ${frequency} ${typeLabel}${recurrenceDay}${reccurenceEndDesc}`;

  return summary;
};

export function formatResidentList(value) {
  if (Array.isArray(value)) {
    return value.map((p) => fToTitleMultipleWords(p.residentName)).join(', ');
  }
  return '';
}
